import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { generarPasswordSeguro as generarPassword, rolLabel } from '../utils/helpers';
import { Tooltip } from './ui';
import SlackMemberIdHelp from './SlackMemberIdHelp';
import toast from 'react-hot-toast';
import { User, Mail, Shield, Lock, Pencil, RefreshCw, Eye, EyeOff, Check, X, Hash } from 'lucide-react';

const Perfil = () => {
    const { user, refreshUser } = useAuth();
    const [editingName, setEditingName] = useState(false);
    const [nombre, setNombre] = useState(user?.nombre || '');
    const [savingName, setSavingName] = useState(false);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [savingPass, setSavingPass] = useState(false);

    const [slackId, setSlackId] = useState(user?.slack_member_id || '');
    const [slackIdEdit, setSlackIdEdit] = useState(false);
    const [savingSlack, setSavingSlack] = useState(false);

    const handleSaveName = async () => {
        if (!nombre.trim()) {
            toast.error('El nombre es requerido');
            return;
        }
        setSavingName(true);
        try {
            await authAPI.updateProfile({ nombre: nombre.trim() });
            toast.success('Nombre actualizado');
            setEditingName(false);
            if (refreshUser) refreshUser();
        } catch (err) {
            toast.error(err.message || 'Error al actualizar nombre');
        } finally {
            setSavingName(false);
        }
    };

    const handleSaveSlackId = async () => {
        setSavingSlack(true);
        try {
            await authAPI.updateProfile({ slack_member_id: slackId.trim() || null });
            toast.success(slackId.trim() ? 'Slack Member ID actualizado' : 'Slack Member ID eliminado');
            setSlackIdEdit(false);
            if (refreshUser) refreshUser();
        } catch (err) {
            toast.error(err.message || 'Error al actualizar Slack Member ID');
        } finally {
            setSavingSlack(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!newPassword) {
            toast.error('Ingresa una contraseña');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('Mínimo 6 caracteres');
            return;
        }
        if (confirmPassword && newPassword !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        setSavingPass(true);
        try {
            await authAPI.changePassword(newPassword);
            toast.success('Contraseña actualizada correctamente');
            setNewPassword('');
            setConfirmPassword('');
            setShowPassword(false);
        } catch (err) {
            toast.error(err.message || 'Error al cambiar contraseña');
        } finally {
            setSavingPass(false);
        }
    };

    const handleGenerar = () => {
        const pass = generarPassword();
        // Generar llena AMBOS campos para que el usuario pueda enviar
        // sin tener que repetir la contraseña manualmente.
        setNewPassword(pass);
        setConfirmPassword(pass);
        setShowPassword(true);
    };

    const rolLabelText = rolLabel(user?.rol);

    return (
        <div className="card">
            <div className="card-header">
                <h2>Mi Perfil</h2>
            </div>

            <div className="admin-content">
                <div className="perfil-info">
                    <div className="perfil-avatar">
                        <User size={48} />
                    </div>
                    <div className="perfil-datos">
                        <div className="perfil-field">
                            <User size={16} />
                            <span className="perfil-label">Nombre</span>
                            {editingName ? (
                                <div className="perfil-edit-row">
                                    <input
                                        className="input"
                                        type="text"
                                        value={nombre}
                                        onChange={(e) => setNombre(e.target.value)}
                                        autoFocus
                                    />
                                    <Tooltip content="Guardar">
                                        <button className="btn-icon edit" onClick={handleSaveName} disabled={savingName} aria-label="Guardar">
                                            <Check size={16} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Cancelar">
                                        <button className="btn-icon text-danger" onClick={() => { setEditingName(false); setNombre(user?.nombre || ''); }} aria-label="Cancelar">
                                            <X size={16} />
                                        </button>
                                    </Tooltip>
                                </div>
                            ) : (
                                <span className="perfil-value">
                                    {user?.nombre}
                                    <Tooltip content="Editar nombre">
                                        <button className="btn-icon edit" onClick={() => setEditingName(true)} aria-label="Editar nombre" style={{ marginLeft: '8px' }}>
                                            <Pencil size={14} />
                                        </button>
                                    </Tooltip>
                                </span>
                            )}
                        </div>
                        <div className="perfil-field">
                            <Mail size={16} />
                            <span className="perfil-label">Email</span>
                            <span className="perfil-value">{user?.email}</span>
                        </div>
                        <div className="perfil-field">
                            <Shield size={16} />
                            <span className="perfil-label">Rol</span>
                            <span className={`badge ${user?.rol === 'superadmin' ? 'warning' : user?.rol === 'admin' ? 'success' : 'info'}`}>
                                {rolLabelText}
                            </span>
                        </div>
                        <div className="perfil-field">
                            <Hash size={16} />
                            <span className="perfil-label">Slack ID</span>
                            {slackIdEdit ? (
                                <div className="perfil-edit-row">
                                    <input
                                        className="input"
                                        type="text"
                                        value={slackId}
                                        onChange={(e) => setSlackId(e.target.value)}
                                        placeholder="U07ABCDE123"
                                        autoFocus
                                    />
                                    <Tooltip content="Guardar">
                                        <button className="btn-icon edit" onClick={handleSaveSlackId} disabled={savingSlack} aria-label="Guardar">
                                            <Check size={16} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content="Cancelar">
                                        <button className="btn-icon text-danger" onClick={() => { setSlackIdEdit(false); setSlackId(user?.slack_member_id || ''); }} aria-label="Cancelar">
                                            <X size={16} />
                                        </button>
                                    </Tooltip>
                                </div>
                            ) : (
                                <span className="perfil-value">
                                    {user?.slack_member_id || <span className="text-muted">Sin configurar</span>}
                                    <Tooltip content={user?.slack_member_id ? 'Editar Slack ID' : 'Agregar Slack ID'}>
                                        <button className="btn-icon edit" onClick={() => setSlackIdEdit(true)} aria-label="Editar Slack ID" style={{ marginLeft: '8px' }}>
                                            <Pencil size={14} />
                                        </button>
                                    </Tooltip>
                                </span>
                            )}
                        </div>
                        {slackIdEdit && (
                            <div style={{ marginLeft: '24px' }}>
                                <SlackMemberIdHelp />
                            </div>
                        )}
                    </div>
                </div>

                <div className="divider" />

                <h3 className="h3 perfil-pass-title">
                    <Lock size={18} aria-hidden="true" />
                    <span>Cambiar Contraseña</span>
                </h3>
                <form onSubmit={handleChangePassword}>
                    <div className="form-row">
                        <label className="field" style={{ flex: 1 }}>
                            <span className="label">Nueva contraseña *</span>
                            <div className="input-password-wrap">
                                <input
                                    className="input"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                />
                                <Tooltip content={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex={-1}
                                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </Tooltip>
                            </div>
                        </label>
                        <label className="field" style={{ flex: 1 }}>
                            <span className="label">Confirmar contraseña</span>
                            <input
                                className="input"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repite la contraseña"
                            />
                        </label>
                    </div>
                    <div className="perfil-pass-actions">
                        <button className="btn neon" type="submit" disabled={savingPass}>
                            {savingPass ? 'Guardando...' : 'Cambiar Contraseña'}
                        </button>
                        <Tooltip content="Generar contraseña segura">
                            <button
                                type="button"
                                className="btn ghost perfil-pass-generar"
                                onClick={handleGenerar}
                            >
                                <RefreshCw size={16} /> Generar
                            </button>
                        </Tooltip>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Perfil;
